import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import useAsyncEffect from 'use-async-effect';
import moment from 'moment';
import { FiArrowLeft, FiArrowRight, FiFileText, FiMail, FiTrendingUp, FiTrendingDown, FiUsers, FiAlertCircle, FiBarChart2, FiChevronDown } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { HoldingsMovementReportSummaryView as Summary } from '../../../model/HoldingsMovementReportResults';
import { GetChartDataResult } from '../../../model/GetChartDataResult';
import { CommunicationsAnnouncementsResult } from '../../../model/CommunicationsAnnouncementsResult';
import axios from 'axios';
import api from '../../../api/api';
import { to } from '../../../util/util';
import { providers } from './AllHolders';
import { EmailReportModal } from './ReportModals';
import { useRegistryData, sharesOnIssue, saveCsv } from './reportsData';

// /share-registry/holdings-movement/{YYYY-MM-DD}: one holdings-movement report in full (Figma 209:6426, note 454:5605;
// Email This Report 532:2828). Opened from Holdings movement's "Open report". Reads only:
// - GET /api/ShareRegistry/holdingsMovementReport — the v1 report page (HTML), parsed here into rows; it regenerates
//   the report from the registry on every call, so the first open can take a while
// - GET /api/ShareRegistry/holdingsMovementReportSummaries — Previous / Next day, shares moved, est. value
// - GET topHolders + registerListing (useRegistryData) — % issued, holder numbers and types
// - GET /api/admin/getChartData — close, volume (admins only; others see the close the report's est. value implies)
// - GET /api/communications/announcements — announcements that day
// Trades, the written insight and report delivery have no source: "—" or "Not connected yet".

type Badge = 'NEW' | 'RETURNING' | 'EXISTING';
interface MoveRow {
    holderId: number | null;
    name: string;
    badge: Badge | null;
    broker: string;
    security: string;
    description: string;
    opening: number;
    net: number;
    closing: number;
}

// "1,234" / "-1,234" / "-" (none) → number
const parseNum = (s: string) => {
    const v = Number((s || '').replace(/[,\s]/g, '').replace('−', '-'));
    return isFinite(v) ? v : 0;
};

// the v1 page's table (TransactionListingReportService.GenerateHoldingsMovementHtml): one row per holder, then a totals row
const parseReport = (html: string): MoveRow[] | null => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    if (!doc.querySelector('table')) return null;
    return Array.from(doc.querySelectorAll('table tr'))
        .filter(tr => !tr.querySelector('th') && !tr.classList.contains('totals'))
        .map(tr => {
            const td = Array.from(tr.querySelectorAll('td'));
            const nameCell = td[0].cloneNode(true) as HTMLElement;
            const badge = nameCell.querySelector('span')?.textContent?.trim().toUpperCase() as Badge | undefined;
            nameCell.querySelectorAll('span').forEach(s => s.remove());
            const id = /holderId=(\d+)/.exec(td[1]?.querySelector('a')?.getAttribute('href') || '');
            return {
                holderId: id ? Number(id[1]) : null,
                name: (nameCell.textContent || '').trim(),
                badge: badge && ['NEW', 'RETURNING', 'EXISTING'].includes(badge) ? badge : null,
                broker: td[2]?.textContent?.trim() || '',
                security: td[3]?.textContent?.trim() || '',
                description: td[4]?.textContent?.trim() || '',
                opening: parseNum(td[5]?.textContent || ''),
                net: parseNum(td[8]?.textContent || ''),
                closing: parseNum(td[10]?.textContent || ''),
            };
        });
};

const num = (n: number) => Math.round(n).toLocaleString();
const signed = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(Math.round(n)).toLocaleString()}`;
const aud = (v?: number | null) => v == null ? '—' : `A$${Math.round(v).toLocaleString()}`;
const holderWord = (n: number) => `${n} holder${n === 1 ? '' : 's'}`;
const FILTERS: Record<string, (r: MoveRow) => boolean> = {
    'All Movements': () => true,
    'Bought': r => r.net > 0,
    'Sold': r => r.net < 0,
    'New and Returning': r => r.badge === 'NEW' || r.badge === 'RETURNING',
    'Exited Completely': r => r.closing === 0 && r.opening > 0,
};
const SHOWN = 10;

interface Market { close: number | null; change: number | null; volume: number | null; volumeRatio: number | null; estimated: boolean }

const MovementReportDetail: React.FC<{ date: string }> = ({ date }) => {
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const history = useHistory();
    const registry = useRegistryData(stock);
    const [rows, setRows] = useState<MoveRow[] | null>(null);
    const [error, setError] = useState('');
    const [reports, setReports] = useState<Summary[] | null>(null);
    const [bars, setBars] = useState<GetChartDataResult | null>(null);
    const [announcements, setAnnouncements] = useState<CommunicationsAnnouncementsResult | null>(null);
    const [filter, setFilter] = useState('All Movements');
    const [unit, setUnit] = useState<'units' | 'value'>('units');
    const [showAll, setShowAll] = useState(false);
    const [emailing, setEmailing] = useState(false);
    const valid = moment(date, 'YYYY-MM-DD', true).isValid();

    useAsyncEffect(async (isMounted: () => boolean) => {
        if (!stock || !valid) return;
        const [[htmlErr, page], [, sums], [, chart], [, anns]] = await Promise.all([
            // IApi has no generic get; the report page is plain HTML on the same signed-in origin, as the v1 link opened it
            to(axios.get(api().getHoldingsMovementReportUrl(stock.ticker, stock.exchange, date), { responseType: 'text' })),
            to(api().getHoldingsMovementReportSummaries(stock.ticker, stock.exchange)),
            to(api().getChartData(stock.id)),
            to(api().getCommunicationsAnnouncements(stock.id)),
        ]);
        if (!isMounted()) return;
        const parsed = typeof page?.data === 'string' ? parseReport(page.data) : null;
        if (htmlErr || !parsed) setError(htmlErr?.response?.data && typeof htmlErr.response.data === 'string' ? htmlErr.response.data : htmlErr?.message || 'This report couldn’t be read');
        else setRows(parsed);
        setReports(sums?.success ? (sums.reports as Summary[]) : []);
        setBars(chart?.stockData ? chart as GetChartDataResult : null);
        setAnnouncements(anns?.rows ? anns as CommunicationsAnnouncementsResult : null);
    }, [stock?.id, date]);

    const company = stock?.companyName || stock?.ticker || '';
    const ticker = stock?.ticker || '';
    const provider = providers[stock?.shareRegistryProvider ?? 0] ?? 'registry';
    const minValue = stock?.shareRegistryOptions?.newHolderMinValue ?? 0;
    const m = moment(date);
    const day = (r: Summary) => moment(r.date).format('YYYY-MM-DD');
    // summaries come newest first
    const index = (reports || []).findIndex(r => day(r) === date);
    const summary = index >= 0 ? reports![index] : null;
    const older = index >= 0 ? reports![index + 1] : (reports || []).find(r => day(r) < date);
    const newer = index > 0 ? reports![index - 1] : index < 0 ? [...(reports || [])].reverse().find(r => day(r) > date) : undefined;
    const open = (d: string) => history.push(`/share-registry/holdings-movement/${d}`);

    // holder numbers / types and % issued from today's register
    const issued = stock ? sharesOnIssue(stock, registry) : 0;
    const listed = useMemo(() => {
        const byId = new Map((registry.listing?.holders || []).map(h => [h.holderId, h]));
        const byName = new Map((registry.listing?.holders || []).map(h => [h.name.toUpperCase(), h]));
        return (r: MoveRow) => (r.holderId != null ? byId.get(r.holderId) : undefined) ?? byName.get(r.name.toUpperCase());
    }, [registry.listing]);

    const market: Market = useMemo(() => {
        const series = [...(bars?.stockData || [])].sort((a, b) => a.date.localeCompare(b.date));
        const i = series.findIndex(b => b.date.slice(0, 10) === date);
        if (i >= 0) {
            const prev = series[i - 1];
            const window = series.slice(Math.max(0, i - 20), i).filter(b => b.volume > 0);
            const avg = window.length ? window.reduce((s, b) => s + b.volume, 0) / window.length : 0;
            return { close: series[i].close, change: prev?.close ? (series[i].close - prev.close) * 100 / prev.close : null,
                volume: series[i].volume, volumeRatio: avg ? series[i].volume / avg : null, estimated: false };
        }
        // est. value = close on (or before) the day × shares moved, so the close falls out of it
        const implied = summary?.estValue != null && summary.totalAbsMovement ? summary.estValue / summary.totalAbsMovement : null;
        return { close: implied, change: null, volume: null, volumeRatio: null, estimated: implied != null };
    }, [bars, summary, date]);

    const sameDay = (announcements?.rows || []).filter(a => moment(a.date).format('YYYY-MM-DD') === date);
    const lastBefore = (announcements?.rows || []).filter(a => moment(a.date).format('YYYY-MM-DD') < date).sort((a, b) => b.date.localeCompare(a.date))[0];

    const back = <button type="button" className="link back" onClick={() => history.push('/share-registry/holdings-movement')}><FiArrowLeft /> Back to Holdings movement</button>;
    if (!valid) return <div className="movement-report-detail">{back}<p className="error">That isn’t a report date.</p></div>;

    const all = rows ? [...rows].sort((a, b) => Math.abs(b.net) - Math.abs(a.net)) : [];
    const bought = all.filter(FILTERS.Bought);
    const sold = all.filter(FILTERS.Sold);
    const exited = all.filter(FILTERS['Exited Completely']);
    const fresh = all.filter(r => r.badge === 'NEW');
    const sharesMoved = summary?.totalAbsMovement ?? bought.reduce((s, r) => s + r.net, 0);
    const filtered = all.filter(FILTERS[filter]);
    const shown = showAll ? filtered : filtered.slice(0, SHOWN);
    const valueOf = (n: number) => market.close == null ? null : n * market.close;
    const change = (r: MoveRow) => unit === 'value' && market.close != null ? `${r.net < 0 ? '−' : '+'}A$${num(Math.abs(valueOf(r.net)!))}` : signed(r.net);
    const pctIssued = (r: MoveRow) => issued && (!r.security || r.security === ticker) ? `${(r.closing * 100 / issued).toFixed(2)}%` : '—';
    const biggestBuy = bought[0];
    const biggestSell = [...sold].sort((a, b) => a.net - b.net)[0];

    const download = () => saveCsv(`${ticker}-holdings-movement-${date}.csv`, [
        ['Holder', 'Holder No.', 'Type', 'Badge', 'Broker', 'Security', 'Description', 'Opening', 'Change', 'Closing', '% Issued'],
        ...all.map(r => [r.name, listed(r)?.holderNumber || '', listed(r)?.holderType || '', r.badge || (FILTERS['Exited Completely'](r) ? 'EXITED' : ''),
            r.broker, r.security, r.description, r.opening, r.net, r.closing, pctIssued(r).replace('—', '')]),
    ]);

    return <div className="movement-report-detail">
        {back}
        <div className="page-header">
            <div>
                <h1>Holdings movement — {m.format('dddd, D MMMM YYYY')}</h1>
                <p>{rows
                    ? [`${all.length} movement${all.length === 1 ? '' : 's'}`, `${num(sharesMoved)} shares`, summary?.estValue != null && `est. ${aud(summary.estValue)}`, 'compared with the previous trading day'].filter(Boolean).join(' · ')
                    : [company, provider !== 'None' && provider].filter(Boolean).join(' · ')}</p>
            </div>
            <div className="mrd-actions">
                <button type="button" className="btn-outline" disabled={!older} onClick={() => older && open(day(older))}>Previous Day</button>
                <button type="button" className="btn-outline" disabled={!newer} onClick={() => newer && open(day(newer))}>Next Day</button>
                <button type="button" className="btn-outline" disabled={!rows} onClick={download}><FiFileText /> Download Report</button>
                <button type="button" className="btn-primary" onClick={() => setEmailing(true)}><FiMail /> Email This Report</button>
            </div>
        </div>

        {error && <p className="error">{error}</p>}
        {!error && !rows && <p className="loading">Loading the report from {provider}… (it’s rebuilt from the registry each time, so this can take a minute)</p>}

        {rows && <>
            <div className="mrd-stats">
                <div className="card mrd-stat"><div className="h"><span className="icon-tile green"><FiTrendingUp /></span>Bought</div>
                    <div className="v">{holderWord(bought.length)}</div><div className="d">{signed(bought.reduce((s, r) => s + r.net, 0))} shares</div></div>
                <div className="card mrd-stat"><div className="h"><span className="icon-tile red"><FiTrendingDown /></span>Sold</div>
                    <div className="v">{holderWord(sold.length)}</div><div className="d">{signed(sold.reduce((s, r) => s + r.net, 0))} shares</div></div>
                <div className="card mrd-stat"><div className="h"><span className="icon-tile"><FiUsers /></span>New holders</div>
                    <div className="v">{fresh.length}</div><div className="d">{fresh.length ? `first-time buyers${minValue ? ` above A$${minValue.toLocaleString()}` : ''}` : `none${minValue ? ` above A$${minValue.toLocaleString()}` : ''}`}</div></div>
                <div className="card mrd-stat"><div className="h"><span className="icon-tile amber"><FiAlertCircle /></span>Exited completely</div>
                    <div className="v">{holderWord(exited.length)}</div><div className="d">sold their whole holding</div></div>
            </div>

            <div className="mrd-layout">
                <section className="card mrd-moves">
                    <div className="mrd-title">
                        <span className="icon-tile"><FiBarChart2 /></span>
                        <div><h2>All Movements on This Day</h2><p>Sorted by size of change{rows.some(r => r.holderId != null) ? ' · click a holder to open them' : ''}</p></div>
                        <div className="mrd-filters">
                            <label className="mrd-select">
                                <select aria-label="Show movements" value={filter} onChange={e => { setFilter(e.target.value); setShowAll(false); }}>{Object.keys(FILTERS).map(f => <option key={f} value={f}>{f}</option>)}</select>
                                <FiChevronDown />
                            </label>
                            <label className="mrd-select">
                                <select aria-label="Units or value" value={unit} onChange={e => setUnit(e.target.value as 'units' | 'value')}>
                                    <option value="units">Show: Units</option>
                                    <option value="value" disabled={market.close == null}>Show: Value (A$)</option>
                                </select>
                                <FiChevronDown />
                            </label>
                        </div>
                    </div>
                    <div className="mrd-table">
                        <div className="mrd-row mrd-head"><span>Holder</span><span className="num">Opening</span><span className="num">Change</span><span className="num">Closing</span><span className="num">% Issued</span></div>
                        {shown.map((r, i) => {
                            const h = listed(r);
                            const id = r.holderId ?? h?.holderId;
                            const exit = FILTERS['Exited Completely'](r);
                            return <div key={i} className={`mrd-row${id ? ' link-row' : ''}`} onClick={() => id && history.push(`/share-registry/holder/${id}`)}>
                                <span className="holder">
                                    <span className="t"><strong>{r.name}</strong><small>{[h?.holderType, h?.holderNumber].filter(Boolean).join(' · ') || r.broker || r.security}</small></span>
                                    {r.badge && <span className={`mrd-badge ${r.badge.toLowerCase()}`}>{r.badge}</span>}
                                    {!r.badge && exit && <span className="mrd-badge exited">EXITED</span>}
                                </span>
                                <span className="num sec">{num(r.opening)}</span>
                                <span className="num"><span className={`mrd-badge ${r.net >= 0 ? 'up' : 'down'}`}>{change(r)}</span></span>
                                <span className="num strong">{num(r.closing)}</span>
                                <span className="num sec">{pctIssued(r)}</span>
                            </div>;
                        })}
                        {filtered.length === 0 && <p className="mrd-empty">No movements of this kind on this day</p>}
                    </div>
                    {filtered.length > SHOWN && <button type="button" className="link mrd-more" onClick={() => setShowAll(!showAll)}>
                        {showAll ? 'Show Fewer' : `Show All ${filtered.length} Movements`} <FiArrowRight />
                    </button>}
                </section>

                <div className="mrd-side">
                    <section className="card mrd-market">
                        <div className="mrd-title"><span className="icon-tile"><FiTrendingUp /></span><div><h2>What Happened on the Market</h2></div></div>
                        <div className="kv"><span>Close</span><span><strong>{market.close == null ? '—' : `A$${market.close.toFixed(market.close < 0.1 ? 4 : 3)}`}</strong>
                            {market.change != null ? <small className={market.change >= 0 ? 'up' : 'down'}>{market.change >= 0 ? '+' : '−'}{Math.abs(market.change).toFixed(2)}%</small>
                                : market.estimated ? <small>implied by the report’s est. value</small> : null}</span></div>
                        <div className="kv"><span>Volume</span><span><strong>{market.volume == null ? '—' : market.volume.toLocaleString()}</strong>
                            {market.volumeRatio != null && <small>{market.volumeRatio.toFixed(1)}× average</small>}</span></div>
                        <div className="kv"><span>Trades</span><span><strong>—</strong><small>not connected yet</small></span></div>
                        <div className="kv"><span>Announcements</span><span>
                            <strong>{!announcements ? '—' : sameDay.length ? `${sameDay.length}` : 'None'}</strong>
                            {sameDay.length > 0 && <small title={sameDay.map(a => a.headline).join('\n')}>{sameDay[0].headline}</small>}
                            {!sameDay.length && lastBefore && <small>last: {moment(lastBefore.date).format('D MMM')}, {lastBefore.headline}</small>}
                        </span></div>
                        {(biggestBuy || biggestSell) && <div className="mrd-insight"><span className="icon-tile"><FiAlertCircle /></span>
                            <p>{biggestBuy && <>The biggest buyer was {biggestBuy.name} ({signed(biggestBuy.net)} shares).</>}
                                {biggestSell && <> The biggest seller was {biggestSell.name} ({signed(biggestSell.net)} shares).</>}</p>
                        </div>}
                    </section>

                    <section className="card mrd-delivery">
                        <div className="mrd-title"><span className="icon-tile"><FiMail /></span><div><h2>Report Delivery</h2></div></div>
                        <p className="mrd-missing">Not connected yet — Investi doesn’t keep a record of who this report was emailed to.</p>
                        <button type="button" className="btn-outline" onClick={() => setEmailing(true)}><FiMail /> Email This Report</button>
                    </section>
                </div>
            </div>
        </>}

        {emailing && stock && <EmailReportModal stock={stock} asAt={m.format('D MMMM YYYY')} onClose={() => setEmailing(false)}
            subject={`${company} — holdings movement, ${m.format('D MMMM YYYY')}`} sub="Sends the movement report as a PDF attachment"
            repeat={{ title: 'Send this every trading day', sub: 'Investi emails it each trading day once the register updates' }} />}
    </div>;
};

export default MovementReportDetail;
